import { inject, Injectable, InjectionToken, signal } from '@angular/core';
import { MediaItem } from './media-api';
import { captureVideoFrame } from './media-file-probe';
import { ProductMediaHttp } from './media-http.service';
import { UploadAbortedError, uploadResumable, UploadTransport } from './media-resume';
import { Sniffed } from './media-sniff';
import { LocalUpload } from './media-tiles';
import { XhrTransport } from './media-xhr-transport';

/** The transport of the file to the cloud; a test hands in its own. */
export const MEDIA_UPLOAD_TRANSPORT = new InjectionToken<UploadTransport>('MEDIA_UPLOAD_TRANSPORT', { providedIn: 'root', factory: () => new XhrTransport() });

/** What the state wants to know about an upload as it goes. */
export interface UploadHooks {
  /** The item is made on the server (UPLOADING) and the file is about to go. */
  started?: (itemId: number) => Promise<void> | void;
  /** The file is all there and the server has queued the item. `preview` is the picture of the file the tile showed (the page keeps it until the server has its own). */
  finished?: (item: MediaItem, preview: string | null) => void;
  /** The server refused to start (limits, type, rights): no item was made. */
  refused?: (error: unknown) => void;
  /** The network failed in the middle of a file: it waits and goes on by itself. */
  reconnecting?: () => void;
  /** The network answered again after `reconnecting`. */
  restored?: () => void;
  /** The upload left the line for any reason (done, refused, stopped, cancelled, discarded): whatever it was waiting for is not waited for any more. */
  ended?: () => void;
  /** The upload stopped on its way (the cloud refused, the network is gone for good, the check at the end failed): the item stays, as a failed tile. */
  failed?: (error: unknown) => string;
}

export interface UploadJob {
  file: File;
  sniffed: Sniffed;
  productInfoId: number;
  replaceId: number | null;
  hooks: UploadHooks;
}

interface Running extends UploadJob {
  uid: number;
  itemId: number | null;
  /** The address of the cloud session: a secret, kept here and nowhere else. */
  uploadUrl: string | null;
  abort: AbortController;
}

/**
 * The files on their way to the cloud, one after the other: `POST /uploads` makes the item and opens the session, one `PUT` of the file goes to the session
 * (resumed after a network error), `POST /{id}/finish` tells the server it is all there. The grid sees only `uploads` (no address, no file).
 */
@Injectable()
export class MediaUploader {
  readonly uploads = signal<LocalUpload[]>([]);

  private readonly api = inject(ProductMediaHttp);
  private readonly transport = inject(MEDIA_UPLOAD_TRANSPORT);
  private readonly jobs = new Map<number, Running>();
  private readonly objectUrls = new Set<string>();
  private seq = 0;
  private working = false;
  private destroyed = false;

  /** Is any file waiting or going (the page warns before it is closed)? */
  get active(): boolean {
    return this.uploads().some(u => u.stage !== 'failed');
  }

  /** Puts a file in the line. Gives the number of its tile. */
  enqueue(job: UploadJob): number {
    const uid = ++this.seq;
    this.jobs.set(uid, { ...job, uid, itemId: null, uploadUrl: null, abort: new AbortController() });
    const preview = job.sniffed.type === 'PHOTO' && job.sniffed.browserReadable ? this.objectUrl(job.file) : null;
    this.uploads.update(list => [
      ...list,
      {
        uid,
        itemId: null,
        fileName: job.file.name,
        kind: job.sniffed.type,
        size: job.file.size,
        sent: 0,
        stage: 'queued',
        replaceId: job.replaceId,
        preview
      }
    ]);
    if (job.sniffed.type === 'VIDEO' && job.sniffed.browserReadable) {
      const url = URL.createObjectURL(job.file);
      captureVideoFrame(url).then(frame => {
        URL.revokeObjectURL(url);
        if (frame) {
          if (this.jobs.has(uid) && !this.destroyed) {
            this.objectUrls.add(frame);
            this.patch(uid, { preview: frame });
          } else {
            URL.revokeObjectURL(frame);
          }
        }
      });
    }
    void this.pump();
    return uid;
  }

  /** The editor stops an upload. Gives the id of the item it had made (the caller removes it on the server). */
  cancel(uid: number): number | null {
    const job = this.jobs.get(uid);
    if (!job) {
      return this.uploads().find(u => u.uid === uid)?.itemId ?? null;
    }
    job.abort.abort();
    return this.drop(uid);
  }

  /** Forgets a stopped upload; gives the id of its item. */
  discard(uid: number): number | null {
    return this.drop(uid);
  }

  /** The job of a stopped upload, to start it again. */
  jobOf(uid: number): UploadJob | null {
    const job = this.jobs.get(uid);
    return job ? { file: job.file, sniffed: job.sniffed, productInfoId: job.productInfoId, replaceId: job.replaceId, hooks: job.hooks } : null;
  }

  /** A picture the page made for an upload may go once the server has its own. */
  releasePreview(url: string): void {
    if (this.objectUrls.delete(url)) {
      URL.revokeObjectURL(url);
    }
  }

  /** The page is gone: stop everything, give the memory back. */
  destroy(): void {
    this.destroyed = true;
    for (const job of this.jobs.values()) {
      job.abort.abort();
    }
    this.jobs.clear();
    for (const url of this.objectUrls) {
      URL.revokeObjectURL(url);
    }
    this.objectUrls.clear();
  }

  private objectUrl(file: Blob): string {
    const url = URL.createObjectURL(file);
    this.objectUrls.add(url);
    return url;
  }

  private patch(uid: number, change: Partial<LocalUpload>): void {
    this.uploads.update(list => list.map(u => (u.uid === uid ? { ...u, ...change } : u)));
  }

  /** Takes an upload out of the line. Its picture is given back to the memory, unless the caller keeps it (`keepPreview`). */
  private drop(uid: number, keepPreview = false): number | null {
    const entry = this.uploads().find(u => u.uid === uid);
    const job = this.jobs.get(uid);
    const itemId = entry?.itemId ?? job?.itemId ?? null;
    this.jobs.delete(uid);
    job?.hooks.ended?.();
    this.uploads.update(list => list.filter(u => u.uid !== uid));
    if (entry?.preview && !keepPreview) {
      this.releasePreview(entry.preview);
    }
    return itemId;
  }

  private async pump(): Promise<void> {
    if (this.working) {
      return;
    }
    this.working = true;
    try {
      for (;;) {
        const next = this.uploads().find(u => u.stage === 'queued' && this.jobs.has(u.uid));
        if (!next || this.destroyed) {
          return;
        }
        await this.run(this.jobs.get(next.uid) as Running);
      }
    } finally {
      this.working = false;
    }
  }

  private async run(job: Running): Promise<void> {
    const { uid, file, sniffed } = job;
    this.patch(uid, { stage: 'starting' });
    let started;
    try {
      started = await this.api.startUpload({
        productInfoId: job.productInfoId,
        type: sniffed.type,
        fileName: file.name,
        contentType: sniffed.contentType,
        size: file.size,
        ...(job.replaceId !== null ? { replaceId: job.replaceId } : {})
      });
    } catch (e) {
      if (this.jobs.has(uid)) {
        this.drop(uid);
        job.hooks.refused?.(e);
      }
      return;
    }
    if (!this.jobs.has(uid)) {
      // cancelled while the server was answering: the item it made is thrown away
      void this.api.remove(started.id).catch(() => undefined);
      return;
    }
    job.itemId = started.id;
    job.uploadUrl = started.uploadUrl;
    this.patch(uid, { itemId: started.id });
    try {
      await job.hooks.started?.(started.id);
      this.patch(uid, { stage: 'sending' });
      let lastPercent = -1;
      await uploadResumable(this.transport, {
        url: started.uploadUrl,
        file,
        contentType: sniffed.contentType,
        signal: job.abort.signal,
        onNetworkError: () => job.hooks.reconnecting?.(),
        onNetworkRestored: () => job.hooks.restored?.(),
        onProgress: (sent, total) => {
          const percent = Math.floor((sent / Math.max(1, total)) * 100);
          if (percent !== lastPercent) {
            lastPercent = percent;
            this.patch(uid, { sent });
          }
        }
      });
      job.uploadUrl = null;
      this.patch(uid, { stage: 'finishing', sent: file.size });
      const item = await this.api.finishUpload(started.id);
      if (this.jobs.has(uid)) {
        const preview = this.uploads().find(u => u.uid === uid)?.preview ?? null;
        this.drop(uid, true);
        job.hooks.finished?.(item, preview);
      }
    } catch (e) {
      job.uploadUrl = null;
      if (e instanceof UploadAbortedError || !this.jobs.has(uid)) {
        return;
      }
      const failure = job.hooks.failed?.(e) ?? '';
      this.patch(uid, { stage: 'failed', failure });
      job.hooks.ended?.();
    }
  }
}
